import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Users, Lock, FileText, Smartphone, Share2 } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";

const APropos = () => {
  const { t, locale } = useLanguage();
  const { hash } = useLocation();

  const arr = (key: string): string[] => {
    const val = t(key, { returnObjects: true, ns: "legal" });
    return Array.isArray(val) ? val : [];
  };

  useSEO({
    title: t("legal:apropos.meta_title"),
    description: t("legal:apropos.meta_desc"),
    canonical: "/a-propos",
    locale,
  });

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container max-w-3xl py-10 space-y-12">
        {/* Qui sommes-nous */}
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <h1 className="font-display text-3xl font-bold">{t("legal:apropos.who_title")}</h1>
          </div>
          <p className="text-muted-foreground leading-relaxed">{t("legal:apropos.who_p1")}</p>
          <p className="text-muted-foreground leading-relaxed">{t("legal:apropos.who_p2")}</p>
          <div className="rounded-lg border bg-muted/50 p-5 space-y-2">
            <p className="font-display font-semibold">{t("legal:apropos.who_box_title")}</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              {arr("legal:apropos.who_goals").map((goal, i) => (
                <li key={i}>{goal}</li>
              ))}
            </ul>
          </div>
          <p className="text-muted-foreground leading-relaxed">{t("legal:apropos.who_values")}</p>
          <p className="text-sm text-muted-foreground italic">{t("legal:apropos.who_disclaimer")}</p>
        </section>

        {/* Application mobile PWA */}
        <section id="application" className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Smartphone className="h-5 w-5 text-primary" />
            </div>
            <h2 className="font-display text-2xl font-bold">{t("legal:apropos.pwa_title")}</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">{t("legal:apropos.pwa_desc")}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Android */}
            <div className="rounded-lg border bg-muted/30 p-5 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#3ddc84]/15">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-[#3ddc84]" aria-hidden="true"><path d="M17.523 15.341 14.63 10.5l2.893-4.841a.5.5 0 0 0-.865-.5L13.766 9.5H10.23L7.342 5.159a.5.5 0 0 0-.865.5L9.37 10.5l-2.893 4.841a.5.5 0 0 0 .865.5L10.234 11.5h3.532l2.892 4.341a.5.5 0 0 0 .865-.5Z"/></svg>
                </div>
                <span className="font-display font-semibold text-sm">{t("legal:apropos.android_title")}</span>
              </div>
              <ol className="space-y-1.5 text-sm text-muted-foreground">
                {arr("legal:apropos.android_steps").map((step, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-semibold text-foreground shrink-0">{i + 1}.</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
            {/* iOS */}
            <div className="rounded-lg border bg-muted/30 p-5 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                  <Share2 className="h-4 w-4 text-primary" aria-hidden="true" />
                </div>
                <span className="font-display font-semibold text-sm">{t("legal:apropos.ios_title")}</span>
              </div>
              <ol className="space-y-1.5 text-sm text-muted-foreground">
                {arr("legal:apropos.ios_steps").map((step, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-semibold text-foreground shrink-0">{i + 1}.</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">{t("legal:apropos.pwa_note")}</p>
        </section>

        {/* Conditions d'utilisation */}
        <section id="conditions" className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <h2 className="font-display text-2xl font-bold">{t("legal:apropos.cgu_title")}</h2>
          </div>
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="font-display text-lg font-semibold">{t("legal:apropos.cgu_s1_title")}</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {arr("legal:apropos.cgu_s1_items").map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
            <div className="space-y-2">
              <h3 className="font-display text-lg font-semibold">{t("legal:apropos.cgu_s2_title")}</h3>
              <p className="text-muted-foreground">{t("legal:apropos.cgu_s2_intro")}</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {arr("legal:apropos.cgu_s2_items").map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <p className="text-sm text-muted-foreground italic">{t("legal:apropos.cgu_s2_note")}</p>
            </div>
            <div className="space-y-2">
              <h3 className="font-display text-lg font-semibold">{t("legal:apropos.cgu_s3_title")}</h3>
              <p className="text-muted-foreground">{t("legal:apropos.cgu_s3_intro")}</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {arr("legal:apropos.cgu_s3_items").map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <p className="text-sm text-muted-foreground italic">{t("legal:apropos.cgu_s3_note")}</p>
            </div>
            <div className="space-y-2">
              <h3 className="font-display text-lg font-semibold">{t("legal:apropos.cgu_s4_title")}</h3>
              <p className="text-muted-foreground">{t("legal:apropos.cgu_s4_body")}</p>
            </div>
          </div>
        </section>

        {/* Politique de confidentialité */}
        <section id="confidentialite" className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Lock className="h-5 w-5 text-primary" />
            </div>
            <h2 className="font-display text-2xl font-bold">{t("legal:apropos.privacy_title")}</h2>
          </div>
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="font-display text-lg font-semibold">{t("legal:apropos.privacy_s1_title")}</h3>
              <p className="text-muted-foreground">{t("legal:apropos.privacy_s1_intro")}</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {arr("legal:apropos.privacy_s1_items").map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
            <div className="space-y-2">
              <h3 className="font-display text-lg font-semibold">{t("legal:apropos.privacy_s2_title")}</h3>
              <p className="text-muted-foreground">{t("legal:apropos.privacy_s2_intro")}</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {arr("legal:apropos.privacy_s2_items").map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <p className="text-sm font-medium text-foreground mt-2">{t("legal:apropos.privacy_s2_note")}</p>
            </div>
            <div className="space-y-2">
              <h3 className="font-display text-lg font-semibold">{t("legal:apropos.privacy_s3_title")}</h3>
              <p className="text-muted-foreground">{t("legal:apropos.privacy_s3_body")}</p>
            </div>
            <div className="space-y-2">
              <h3 className="font-display text-lg font-semibold">{t("legal:apropos.privacy_s4_title")}</h3>
              <p className="text-muted-foreground">{t("legal:apropos.privacy_s4_intro")}</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {arr("legal:apropos.privacy_s4_items").map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default APropos;
